// Client-Side Excel Template Importer Component per ORIGINAL_REQUEST § R2 & PROJECT.md F2.3
import React, { useState, useRef } from 'react';
import {
  parseExcelWorkbook,
  generateTemplateFromParsedWorkbook,
  validateChecklistTemplate,
  type ParsedWorkbookResult,
} from '../../services/excel';
import { saveTemplate, saveMachine, getAllMachines } from '../../db';
import type { Machine } from '../../types';
import { Button, Card } from '../common';
import {
  FileSpreadsheet,
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  Gauge,
  CheckSquare,
  ListFilter,
  FileCheck,
  Layers,
  ArrowRight,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { broadcastSystemData } from '../../services/config/cloudConfigService';

export interface ExcelImporterProps {
  onImportSuccess?: (templateId: string) => void;
}

export const ExcelImporter: React.FC<ExcelImporterProps> = ({ onImportSuccess }) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isParsing, setIsParsing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [parsedResult, setParsedResult] = useState<ParsedWorkbookResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');

  // Metadata form
  const [templateId, setTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>('');
  const [machineModel, setMachineModel] = useState<string>('');
  const [templateVersion, setTemplateVersion] = useState<string>('1.0.0');
  const [autoCreateMachine, setAutoCreateMachine] = useState<boolean>(true);
  const [machineId, setMachineId] = useState<string>('');
  const [previewTab, setPreviewTab] = useState<string>('ALL');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = async (file: File) => {
    setIsParsing(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const result = await parseExcelWorkbook(file);
      if (result.allItems.length === 0) {
        throw new Error('ไม่พบรายการตรวจเช็กที่สามารถอ่านได้ในไฟล์ Excel นี้ กรุณาตรวจสอบตาราง 7 คอลัมน์');
      }

      setParsedResult(result);

      // Auto-populate metadata based on filename
      const cleanFileName = file.name.replace(/\.[^/.]+$/, '');
      const slug = cleanFileName.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const generatedTplId = `tpl_${slug || 'custom'}_${Date.now().toString().slice(-4)}`;

      setTemplateId(generatedTplId);
      setTemplateName(cleanFileName.replace(/_/g, ' '));
      setMachineModel(cleanFileName.split('_')[0].toUpperCase() || 'CNC-MACHINE');
      setMachineId(`CNC-${Math.floor(10 + Math.random() * 90)}`);
      setPreviewTab('ALL');
    } catch (err: any) {
      console.error('[ExcelImporter] Parse failed:', err);
      setErrorMsg(err.message || 'เกิดข้อผิดพลาดในการประมวลผลไฟล์ Excel');
      setParsedResult(null);
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleSaveImportedTemplate = async () => {
    if (!parsedResult) return;
    setErrorMsg('');
    setSuccessMsg('');

    if (!templateName.trim()) {
      setErrorMsg('กรุณาระบุชื่อเทมเพลต');
      return;
    }
    if (!machineModel.trim()) {
      setErrorMsg('กรุณาระบุรุ่นเครื่องจักร');
      return;
    }

    setIsSaving(true);
    try {
      const finalTemplate = generateTemplateFromParsedWorkbook(parsedResult, {
        id: templateId.trim() || undefined,
        name: templateName.trim(),
        machineModel: machineModel.trim(),
        version: templateVersion.trim() || '1.0.0',
      });

      const validation = validateChecklistTemplate(finalTemplate);
      if (!validation.valid) {
        throw new Error(`เทมเพลตไม่ผ่านการตรวจสอบ: ${validation.errors.join(', ')}`);
      }

      await saveTemplate(finalTemplate);

      // Auto-create machine profile if requested
      if (autoCreateMachine && machineId.trim()) {
        const now = new Date().toISOString();
        const newMachine: Machine = {
          id: machineId.trim(),
          name: `${machineModel.trim()} (Auto-imported)`,
          model: machineModel.trim(),
          serialNo: `SN-${Date.now().toString().slice(-6)}`,
          assignedTemplateId: finalTemplate.id,
          cumulativeHours: 0,
          status: 'NORMAL',
          createdAt: now,
          updatedAt: now,
        };
        await saveMachine(newMachine);
      }

      // Broadcast newly imported template and machine to Server & Google Sheets
      broadcastSystemData({
        reason: `นำเข้าเทมเพลต Excel: ${finalTemplate.name}${autoCreateMachine ? ` และเครื่อง ${machineId}` : ''}`,
      }).catch(() => {});

      setSuccessMsg(
        `นำเข้าเทมเพลต "${finalTemplate.name}" สำเร็จ (${finalTemplate.items.length} รายการ)${
          autoCreateMachine ? ` พร้อมสร้างเครื่องจักร ${machineId}` : ''
        } และกระจายไปยังทุกเครื่องแล้ว`
      );

      if (onImportSuccess) {
        onImportSuccess(finalTemplate.id);
      }
    } catch (err: any) {
      console.error('[ExcelImporter] Save failed:', err);
      setErrorMsg(err.message || 'บันทึกลงฐานข้อมูลไม่สำเร็จ');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredItems = parsedResult
    ? previewTab === 'ALL'
      ? parsedResult.allItems
      : parsedResult.allItems.filter((i) => i.frequency === previewTab)
    : [];

  return (
    <div className="space-y-6 animate-in fade-in duration-150">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-emerald-400" />
          เครื่องมือนำเข้าเทมเพลต Excel (.xlsx Importer)
        </h2>
        <p className="text-xs text-slate-400 mt-0.5">
          อัปโหลดไฟล์ตารางเช็กชีตมาตรฐาน 7 คอลัมน์ (SheetJS) เพื่อแปลงเป็นเทมเพลตตรวจเช็กแบบไดนามิกพร้อมระบบอนุมานประเภทข้อมูลอัตโนมัติ
        </p>
      </div>

      {/* Notifications */}
      {successMsg && (
        <div className="p-4 bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-sm rounded-xl flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 shrink-0 text-emerald-400" />
          <div className="flex-1">
            <p className="font-bold">นำเข้าข้อมูลสำเร็จ!</p>
            <p className="text-xs text-emerald-400/80 mt-0.5">{successMsg}</p>
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="p-4 bg-rose-950/80 border border-rose-800 text-rose-300 text-sm rounded-xl flex items-center gap-3">
          <AlertCircle className="w-6 h-6 shrink-0 text-rose-400" />
          <div className="flex-1">
            <p className="font-bold">เกิดข้อผิดพลาดในการนำเข้า</p>
            <p className="text-xs text-rose-400/80 mt-0.5">{errorMsg}</p>
          </div>
        </div>
      )}

      {/* Drop Zone */}
      {!parsedResult ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 select-none ${
            isDragging
              ? 'border-emerald-500 bg-emerald-50 scale-[0.99]'
              : 'border-slate-300 hover:border-slate-400 bg-slate-50/60 hover:bg-slate-50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            className="hidden"
          />

          <div className="flex flex-col items-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 mb-4 shadow-sm">
              {isParsing ? (
                <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
              ) : (
                <UploadCloud className="w-8 h-8" />
              )}
            </div>

            <h3 className="text-base font-bold text-slate-900">
              {isParsing ? 'กำลังอ่านและวิเคราะห์ไฟล์ Excel...' : 'คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวางที่นี่'}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              รองรับไฟล์ Excel (.xlsx, .xls) เลย์เอาต์มาตรฐาน 7 คอลัมน์ (No., Task, Criteria, Method, Value, Status, Notes)
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-[11px] text-slate-600">
              <span className="px-2.5 py-1 rounded bg-slate-100 border border-slate-200">
                ✓ Daily & Shift
              </span>
              <span className="px-2.5 py-1 rounded bg-slate-100 border border-slate-200">
                ✓ Weekly
              </span>
              <span className="px-2.5 py-1 rounded bg-slate-100 border border-slate-200">
                ✓ Monthly
              </span>
              <span className="px-2.5 py-1 rounded bg-slate-100 border border-slate-200">
                ✓ Operating Hours
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* Parsed Preview and Confirmation Card */
        <div className="space-y-6">
          <Card
            title={
              <div className="flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-emerald-600" />
                <span className="text-slate-900 font-bold">สรุปผลการวิเคราะห์ไฟล์: {parsedResult.fileName}</span>
              </div>
            }
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setParsedResult(null);
                  setErrorMsg('');
                  setSuccessMsg('');
                }}
                leftIcon={<RefreshCw className="w-4 h-4" />}
              >
                เลือกไฟล์ใหม่
              </Button>
            }
          >
            {/* Breakdown Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                <span className="text-xs text-slate-500 block mb-1 font-medium">รวมทั้งหมด</span>
                <span className="font-mono text-xl font-bold text-slate-900">
                  {parsedResult.summary.totalItems}
                </span>
                <span className="text-[10px] text-slate-500 block">รายการ</span>
              </div>
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                <span className="text-xs text-slate-500 block mb-1 font-medium">ประจำวัน</span>
                <span className="font-mono text-xl font-bold text-emerald-700">
                  {parsedResult.summary.dailyCount}
                </span>
                <span className="text-[10px] text-slate-500 block">Daily</span>
              </div>
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                <span className="text-xs text-slate-500 block mb-1 font-medium">ประจำสัปดาห์</span>
                <span className="font-mono text-xl font-bold text-blue-700">
                  {parsedResult.summary.weeklyCount}
                </span>
                <span className="text-[10px] text-slate-500 block">Weekly</span>
              </div>
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                <span className="text-xs text-slate-500 block mb-1 font-medium">ประจำเดือน</span>
                <span className="font-mono text-xl font-bold text-purple-700">
                  {parsedResult.summary.monthlyCount}
                </span>
                <span className="text-[10px] text-slate-500 block">Monthly</span>
              </div>
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center col-span-2 sm:col-span-1">
                <span className="text-xs text-slate-500 block mb-1 font-medium">รอบชั่วโมง</span>
                <span className="font-mono text-xl font-bold text-amber-700">
                  {parsedResult.summary.hoursCount}
                </span>
                <span className="text-[10px] text-slate-500 block">Hours</span>
              </div>
            </div>

            {/* Template Metadata Form */}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
              <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-emerald-600" />
                ตั้งค่าข้อมูลเทมเพลตที่สร้างใหม่
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    รหัสเทมเพลต (Template ID) *
                  </label>
                  <input
                    type="text"
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] font-mono focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    ชื่อเทมเพลต (Template Name) *
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    รุ่นเครื่องจักรที่รองรับ (Target Model) *
                  </label>
                  <input
                    type="text"
                    value={machineModel}
                    onChange={(e) => setMachineModel(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    เวอร์ชัน (Version)
                  </label>
                  <input
                    type="text"
                    value={templateVersion}
                    onChange={(e) => setTemplateVersion(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 min-h-[44px] font-mono focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Auto Create Machine Option */}
              <div className="pt-2 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoCreateMachine}
                    onChange={(e) => setAutoCreateMachine(e.target.checked)}
                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 bg-white border-slate-300"
                  />
                  <span className="text-xs text-slate-700 font-medium">
                    สร้างโปรไฟล์เครื่องจักรใหม่และผูกเข้ากับเทมเพลตนี้ทันที
                  </span>
                </label>

                {autoCreateMachine && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600">รหัสเครื่อง:</span>
                    <input
                      type="text"
                      value={machineId}
                      onChange={(e) => setMachineId(e.target.value)}
                      placeholder="CNC-02"
                      className="w-28 bg-white border border-slate-300 rounded px-2 py-1 text-xs font-mono text-slate-900 text-center"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex items-center justify-end gap-3">
              <Button
                variant="primary"
                size="lg"
                onClick={handleSaveImportedTemplate}
                isLoading={isSaving}
                leftIcon={<CheckCircle2 className="w-5 h-5" />}
              >
                ยืนยันนำเข้าและบันทึกสู่ฐานข้อมูล ({parsedResult.allItems.length} รายการ)
              </Button>
            </div>
          </Card>

          {/* Items Preview Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <h3 className="text-sm font-bold text-slate-800">
                พรีวิวรายการที่สกัดได้จากตาราง ({filteredItems.length} รายการ)
              </h3>

              <div className="flex gap-1">
                {['ALL', 'DAILY', 'WEEKLY', 'MONTHLY', 'HOURS'].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setPreviewTab(tab)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      previewTab === tab
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
              {filteredItems.map((item, idx) => (
                <div key={idx} className="p-3.5 hover:bg-slate-50 flex items-start gap-3 text-xs">
                  <span className="font-mono font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded border border-slate-200">
                    #{item.itemNo}
                  </span>

                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-800">{item.taskNameTh}</span>
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        {item.frequency}
                      </span>
                      {item.hoursThreshold && (
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                          {item.hoursThreshold}h
                        </span>
                      )}
                    </div>

                    <p className="text-slate-600">
                      <strong className="text-slate-500">เกณฑ์: </strong> {item.criteriaTh} |{' '}
                      <strong className="text-slate-500">วิธี: </strong> {item.methodTh}
                    </p>
                  </div>

                  <div className="shrink-0 text-right">
                    {item.inputType === 'numeric_gauge' && (
                      <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono text-[11px] inline-flex items-center gap-1">
                        <Gauge className="w-3 h-3" /> เกจ ({item.unit}
                        {item.minValue !== undefined ? ` >= ${item.minValue}` : ''})
                      </span>
                    )}
                    {item.inputType === 'select_option' && (
                      <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] inline-flex items-center gap-1">
                        <ListFilter className="w-3 h-3" /> {item.options?.join('/')}
                      </span>
                    )}
                    {item.inputType === 'done_checkbox' && (
                      <span className="px-2 py-1 rounded bg-purple-50 text-purple-700 border border-purple-200 text-[11px] inline-flex items-center gap-1">
                        <CheckSquare className="w-3 h-3" /> Checkbox
                      </span>
                    )}
                    {item.inputType === 'text' && (
                      <span className="px-2 py-1 rounded bg-slate-100 text-slate-600 text-[11px]">
                        Text
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
